import React, { useState } from "react";
import { Heart, Home, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "./Buttons/Button.js";
import UserChoiceCard from "./UserChoiceCard.js";
import AppModalCard from "./shared/AppModalCard.js";
import AppModalHeader from "./shared/AppModalHeader.js";
import AppModalShell from "./shared/AppModalShell.js";

interface UserTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectType: (type: "adopter" | "breeder" | "visitor") => void;
}

const UserTypeModal: React.FC<UserTypeModalProps> = ({
  isOpen,
  onClose,
  onSelectType,
}) => {
  const { t } = useTranslation(["onboarding", "common"]);
  const [selectedType, setSelectedType] = useState<
    "adopter" | "breeder" | "visitor" | null
  >(null);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (selectedType) {
      onSelectType(selectedType);
      onClose();
    }
  };

  const userTypes = [
    {
      type: "adopter" as const,
      title: t("userTypeModal.adopter.title"),
      description: t("userTypeModal.adopter.description"),
      Icon: Heart,
      iconClass: "text-main-purple",
    },
    {
      type: "breeder" as const,
      title: t("userTypeModal.breeder.title"),
      description: t("userTypeModal.breeder.description"),
      Icon: Home,
      iconClass: "text-azul-gato",
    },
    {
      type: "visitor" as const,
      title: t("userTypeModal.visitor.title"),
      description: t("userTypeModal.visitor.description"),
      Icon: Eye,
      iconClass: "text-golden",
    },
  ];

  return (
    <AppModalShell onBackdropClick={onClose} overlayClassName="bg-black/50" zIndexClassName="z-[10000]">
      <AppModalCard size="sm" panelClassName="flex max-h-[90dvh] flex-col overflow-y-auto">
        <AppModalHeader
          title={
            <div className="text-center">
              <h2 className="text-xl font-bold text-my-black">
                {t("userTypeModal.welcome")}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {t("userTypeModal.intro")}
              </p>
            </div>
          }
          onClose={onClose}
          align="center"
          className="items-start border-b-0 px-5 pb-3 pt-5"
          titleClassName="w-full"
        />

        <div className="px-5 pb-3 flex flex-col gap-3">
          {userTypes.map(({ type, title, description, Icon, iconClass }) => {
            const isSelected = selectedType === type;
            return (
              <UserChoiceCard
                key={type}
                title={title}
                description={description}
                Icon={Icon}
                iconClassName={iconClass}
                selected={isSelected}
                onClick={() => setSelectedType(type)}
                variant="modal"
              />
            );
          })}
        </div>

        <div className="px-5 pb-5 pt-2 flex flex-col gap-3">
          <p className="text-xs text-gray-400 text-center">
            {t("userTypeModal.footnote")}
          </p>
          <Button
            onClick={handleSubmit}
            disabled={!selectedType}
            className={`w-full text-base font-bold transition-all duration-200 ${
              selectedType
                ? "bg-main-purple hover:bg-my-black text-white shadow-md"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            {t("common:actions.continue")}
          </Button>
        </div>
      </AppModalCard>
    </AppModalShell>
  );
};

export default UserTypeModal;
